/**
 * Help content shown when an agency user opens the help drawer.
 * Keep it scan-able — agencies live in this app and want answers fast.
 */
export function AgencyHelpContent() {
  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold text-scope-navy">Keyboard shortcuts</h3>
        <Table
          rows={[
            ['Cmd / Ctrl + K', 'Global search'],
            ['/', 'Focus the search field on any list'],
            ['Esc', 'Close any open modal / drawer'],
            ['Cmd / Ctrl + Enter', 'Submit the active form'],
          ]}
        />
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-scope-navy">Who can do what</h3>
        <Table
          rows={[
            ['Super admin', 'Everything Admin can, plus agency settings'],
            [
              'Admin',
              'Create / edit / delete clients, engagements, projects, deliverables, reports, invoices; invite users + contacts',
            ],
            ['Contributor', 'Log deliverables, upload documents, create reports'],
          ]}
        />
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-scope-navy">Useful to remember</h3>
        <ul className="list-disc space-y-2 pl-5 text-xs">
          <li>
            <strong>Recurring deliverables</strong> (e.g. "Google Ads management") accrue value only
            at calendar month-end. A service starting mid-May contributes nothing until 1 June.
          </li>
          <li>
            <strong>Retainer paid</strong> is inferred from{' '}
            <code className="text-2xs">monthlyRetainerValue × completed months</code> until you log
            an invoice. Once any non-VOID invoice exists, paid = sum of PAID invoices.
          </li>
          <li>
            <strong>All values are stored ex VAT.</strong> The agency's default VAT rate drives
            "(inc VAT)" display hints only.
          </li>
          <li>
            <strong>Notifications</strong> are also emailed to the recipient, and pushed if they've
            installed the PWA + accepted push.
          </li>
          <li>
            <strong>The audit log</strong> at <code className="text-2xs">/audit</code> is
            append-only — no edits, no deletes.
          </li>
        </ul>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-scope-navy">Need more?</h3>
        <p className="text-xs">
          The full user guide is in{' '}
          <a
            href="https://github.com/antonmann/scope/blob/main/docs/USER_GUIDE.md"
            target="_blank"
            rel="noreferrer"
            className="text-scope-green hover:underline"
          >
            docs/USER_GUIDE.md
          </a>
          .
        </p>
      </section>
    </div>
  );
}

function Table({ rows }: { rows: string[][] }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-xs">
      {rows.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="font-mono text-scope-navy">{k}</dt>
          <dd className="text-scope-text-muted">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
